<template>
    <Head title="Design System" />
    <div class="mx-auto w-full max-w-[1320px] px-6 pb-10 md:px-10" :style="{ '--ds-toolbar-height': `${toolbarHeight}px` }">
        <div class="flex flex-col gap-2 pt-6">
            <BasePageTitle title="Design system" subtitle="Internal reference for the components, typography and surfaces available in the product." no-border />
        </div>

        <div ref="toolbar" class="ds-toolbar">
            <div ref="searchWrapper" class="w-full md:max-w-96">
                <BaseInput v-model="query" type="search" aria-label="Filter components" :placeholder="`Filter components — press ${filterShortcutHint} to focus`">
                    <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
                </BaseInput>
            </div>
            <p aria-live="polite" aria-atomic="true" class="sr-only">{{ filterStatus }}</p>
            <nav aria-label="Jump to section" class="ds-pills lg:hidden">
                <a v-for="section in visibleSections" :key="section.id" :href="`#${section.id}`" class="ds-pill">{{ section.title }}</a>
            </nav>
        </div>

        <div class="grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,1fr)_11rem]">
            <div class="flex flex-col gap-10">
                <p v-if="visibleSections.length === 0" class="body text-ink-muted">No components match “{{ query }}”.</p>

                <DesignSystemSection id="actions" title="Actions" description="Things a person clicks to do something.">
                    <DesignSystemEntry
                        name="Button"
                        description="Themes supply the default appearance and geometry. Enabled buttons subtly scale while pressed unless reduced motion is preferred. Use size and icon-only for action controls, BaseLink for navigation, and consumer classes for custom compositions.">
                        <div class="mb-4">
                            <BaseSelect v-model="selectedButtonTheme" :options="buttonThemeOptions" />
                        </div>
                        <div class="flex flex-wrap gap-2 mb-2 items-start">
                            <BaseButton :theme="selectedButtonTheme">{{ selectedButtonTheme }}</BaseButton>
                            <BaseButton :theme="selectedButtonTheme" disabled>{{ selectedButtonTheme }} (disabled)</BaseButton>
                            <BaseButton :theme="selectedButtonTheme" size="sm">{{ selectedButtonTheme }} small</BaseButton>
                        </div>
                        <p class="body-small text-ink-muted mb-2">
                            For icon-and-label compositions, add base-btn-with-icon. Add base-btn-icon-start for a 6px icon-to-label gap and 12px leading icon inset. These
                            utilities work with BaseButton and button-styled BaseLink at both sizes.
                        </p>
                        <div class="flex flex-wrap gap-2 mb-2 items-center">
                            <BaseButton :theme="selectedButtonTheme" class="base-btn-with-icon base-btn-icon-start"><i class="bi bi-plus-lg" aria-hidden="true"></i>New</BaseButton>
                            <BaseButton :theme="selectedButtonTheme" size="sm" class="base-btn-with-icon base-btn-icon-start"
                                ><i class="bi bi-plus-lg" aria-hidden="true"></i>New</BaseButton
                            >
                        </div>
                        <div class="flex flex-wrap gap-2 mb-2 items-center">
                            <BaseButton :theme="selectedButtonTheme" icon-only aria-label="Add">
                                <i class="bi bi-plus-lg" aria-hidden="true"></i>
                            </BaseButton>
                            <BaseButton :theme="selectedButtonTheme" icon-only size="sm" aria-label="More options">
                                <i class="bi bi-three-dots" aria-hidden="true"></i>
                            </BaseButton>
                            <BaseButton :theme="selectedButtonTheme" icon-only disabled aria-label="Add">
                                <i class="bi bi-plus-lg" aria-hidden="true"></i>
                            </BaseButton>
                            <span class="body-small text-ink-muted">icon-only, icon-only small, icon-only disabled</span>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Link"
                        keywords="anchor text link underline"
                        description="Text links take the base-link class on a BaseLink or a plain <a>. They inherit the surrounding type, and the underline darkens on hover.">
                        <p class="body mb-2">
                            Your collaboration profile includes your work style and strengths.
                            <BaseLink href="/design-system/" class="base-link">See it in action</BaseLink>
                        </p>
                        <p class="body-small text-ink-muted mb-2">
                            Smaller copy, external link:
                            <a href="https://design.valence.co/" target="_blank" rel="noopener" class="base-link">Valence design system</a>
                        </p>
                        <BaseLink href="/design-system/" class="base-link body-small-bold">Standalone link</BaseLink>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Dropdown Menu"
                        keywords="action menu context menu"
                        description="an action menu configured as sections with keyboard navigation, automatic separators, and item-specific content slots">
                        <div class="flex gap-2">
                            <BaseDropdownMenu :sections="dropdownMenuSections" @select="handleDropdownMenuSelect">
                                <template #trigger><BaseButton theme="primary">Actions</BaseButton></template>
                            </BaseDropdownMenu>
                            <BaseDropdownMenu :sections="dropdownMenuInstanceSections" label="Switch instance" align="start" @select="handleDropdownMenuSelect">
                                <template #trigger><BaseButton theme="tertiary">With a label</BaseButton></template>
                                <template #item-current-instance="{ item }">
                                    <i class="bi bi-check2 shrink-0 text-base leading-none" aria-hidden="true"></i>
                                    {{ item.label }} <span class="body-small">(current)</span>
                                </template>
                            </BaseDropdownMenu>
                        </div>
                        <span><strong>last action:</strong> {{ lastMenuAction }}</span>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Toggle Group"
                        keywords="segmented control, filter chips, tiles, multi-select"
                        description="A group of toggle buttons. `type` is single or multiple; `appearance` is segmented (pick one), chips or tiles. At least one stays selected unless `required` is false.">
                        <BaseToggleGroup v-model="activeAlignment" :options="alignments" aria-label="Alignment, segmented" />
                        <BaseToggleGroup v-model="activeAlignment" :options="alignments" appearance="chips" aria-label="Alignment as chips" />
                        <BaseToggleGroup v-model="activeAlignments" :options="alignments" type="multiple" appearance="chips" aria-label="Alignments, multiple chips" />
                        <BaseToggleGroup v-model="activeAlignment" :options="alignments" appearance="tiles" aria-label="Alignment as tiles" />
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="forms" title="Forms and inputs" description="Controls that collect a value.">
                    <DesignSystemEntry name="Dropdown" keywords="select" description="a simple key/label scrollable dropdown component, with optional icon">
                        <div class="flex gap-2">
                            <BaseSelect v-model="dropdownSelected" :options="dropdownItems" icon="bi-people-fill" />
                            <BaseSelect v-model="dropdownSelected" :options="dropdownItems" icon="bi-people-fill" dropdown-size="tall" aria-label="Tall dropdown" />
                            <BaseSelect placeholder="(Disabled)" disabled />
                            <BaseSelect v-model="dropdownSelected" :options="dropdownItems" prefix="Sort:" aria-label="Sort with inline label" />
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Combobox"
                        keywords="autocomplete typeahead"
                        description="a combobox component, which is like a select component with search and autocomplete">
                        <div class="flex flex-col gap-2">
                            <!-- uses default {value: string, label: string} -->
                            <BaseCombobox v-model="selectedDefaultCat" :options="defaultCats"></BaseCombobox>
                            <span><strong>selected:</strong> {{ selectedDefaultCat }}</span>
                            <!-- uses custom string[] -->
                            <BaseCombobox v-model="selectedSimpleCat" :options="simpleCats" :display-value="(option) => option" :key-value="(option) => option"></BaseCombobox>
                            <span><strong>selected:</strong> {{ selectedSimpleCat }}</span>
                            <!-- action rows: affordances that produce a value rather than values themselves -->
                            <BaseCombobox
                                v-model="selectedActionCat"
                                v-model:input="catSearch"
                                :options="defaultCats"
                                :actions="catActions"
                                placeholder="Search or add a cat"
                                @action="lastCatAction = $event">
                                <template #action="{ action }">
                                    <i class="bi bi-plus-lg mr-2" aria-hidden="true"></i>
                                    <span>{{ action.label }}</span>
                                </template>
                            </BaseCombobox>
                            <span><strong>selected:</strong> {{ selectedActionCat }}</span>
                            <span><strong>last action:</strong> {{ lastCatAction ?? "(none)" }}</span>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Search Input"
                        keywords="BaseInput search prefix suffix magnifier"
                        description="A BaseInput with type='search' and the search glyph in its prefix slot. Full width by default; size='compact' for header placements. Inside a FormField it takes the label, hint and error like any other input.">
                        <div class="flex flex-col gap-3">
                            <BaseInput v-model="designSystemSearch" type="search" aria-label="Search example" placeholder="Search for team members">
                                <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
                            </BaseInput>
                            <BaseInput v-model="designSystemSearch" type="search" size="compact" aria-label="Compact search example" placeholder="Search">
                                <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
                            </BaseInput>
                            <FormField label="Search people" hint="Search by name or job title.">
                                <BaseInput v-model="designSystemSearch" type="search" placeholder="Search for team members">
                                    <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
                                </BaseInput>
                            </FormField>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Form field"
                        keywords="input text label hint error required checkbox group"
                        description="Wrapper that owns the label, hint, error and aria wiring for whatever control sits in its slot; BaseInput is the plain single-line text input, with size='compact' for headers and size='large' for onboarding steps.">
                        <div class="flex flex-col gap-6">
                            <BaseInput v-model="formFieldName" aria-label="Bare input" placeholder="Bare BaseInput (label via aria-label)" />
                            <FormField label="Full name">
                                <BaseInput v-model="formFieldName" placeholder="Jane Doe" autocomplete="name" />
                            </FormField>
                            <FormField label="Full name" hint="size='large' is the roomy one-question field on onboarding steps, with body type.">
                                <BaseInput v-model="formFieldName" size="large" placeholder="Jane Doe" autocomplete="name" />
                            </FormField>
                            <FormField label="Full name" hint="weight='bold' sets the typed text in semibold, for a field that is the whole question on screen.">
                                <BaseInput v-model="formFieldName" size="large" weight="bold" placeholder="Jane Doe" autocomplete="name" />
                            </FormField>
                            <FormField label="Work email" hint="We only use this for meeting invites.">
                                <BaseInput v-model="formFieldEmail" type="email" placeholder="jane@example.com" />
                            </FormField>
                            <FormField label="Work email" error="Enter a valid email address.">
                                <BaseInput v-model="formFieldEmail" type="email" />
                            </FormField>
                            <FormField label="Full name" required>
                                <BaseInput v-model="formFieldName" />
                            </FormField>
                            <FormField label="Team members" note="(at least 1 person)" required>
                                <BaseInput v-model="formFieldName" />
                            </FormField>
                            <FormField label="Search people" hide-label hint="The label is visually hidden but still announced.">
                                <BaseInput v-model="formFieldName" placeholder="Hidden label" />
                            </FormField>
                            <FormField label="Notes" hint="Wraps BaseTextarea the same way.">
                                <BaseTextarea v-model:input="formFieldNotes" disable-autosize rows="3" />
                            </FormField>
                            <FormField label="Team">
                                <BaseSelect v-model="formFieldTeam" :options="dropdownItems" placeholder="Pick a team" />
                            </FormField>
                            <FormField label="Rater" error="Choose someone to rate you.">
                                <BaseCombobox v-model="formFieldRater" :options="defaultCats" placeholder="Search people" />
                            </FormField>
                            <FormField label="Favorite fruit" required error="Choose a fruit.">
                                <BaseRadioGroup v-model="formFieldFruit" :options="radioFruitOptions" />
                            </FormField>
                            <FormField label="Alignment" error="This alignment is not available for your team.">
                                <BaseToggleGroup v-model="formFieldAlignment" :options="alignments" />
                            </FormField>
                            <FormField label="Weekly digest" error="Turn on the digest so your team gets the summary.">
                                <BaseSwitch v-model="formFieldDigest" size="md" />
                            </FormField>
                            <FormField label="Structure" error="Move the slider to pick how much structure you want.">
                                <BaseSlider v-model="formFieldStructure" :min="0" :max="5" :step="1" label-start="Loose" label-end="Tight" />
                            </FormField>
                            <FormField label="Terms" error="Accept the terms to continue.">
                                <BaseCheckbox v-model="formFieldTerms" shape="square">I accept these Terms of Consent</BaseCheckbox>
                            </FormField>
                            <FormField label="Meeting days" error="Pick at least one day.">
                                <BaseCheckboxGroup>
                                    <BaseCheckbox v-for="day in formFieldDayOptions" :key="day" v-model="formFieldDays[day]" shape="square">{{ day }}</BaseCheckbox>
                                </BaseCheckboxGroup>
                            </FormField>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Base Checkbox" keywords="checkbox tick" description="A simple checkbox component that can be used in forms.">
                        <BaseCheckbox v-model="isCheckboxChecked">Check me out</BaseCheckbox>
                        <BaseCheckbox v-model="isCheckboxCheckedFilled" :is-filled="false">Check me out</BaseCheckbox>
                        <BaseCheckbox v-model="isCheckboxCheckedSquare" shape="square">Check me out</BaseCheckbox>
                        <BaseCheckbox v-model="isCheckboxCheckedSquareFilled" :is-filled="false" shape="square">Check me out</BaseCheckbox>
                        <BaseCheckbox v-model="isCheckboxIndeterminate">Some of us are checked</BaseCheckbox>
                        <BaseCheckbox v-model="isCheckboxSquareIndeterminate" shape="square">Some of us are checked</BaseCheckbox>
                        <BaseCheckbox :model-value="true" indicator>Indicator only, for a parent control that owns the state</BaseCheckbox>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Radio Group"
                        description="A single-select radio group built on reka-ui primitives. Supports vertical/horizontal orientation and disabled options/groups.">
                        <div class="flex flex-col gap-6">
                            <BaseRadioGroup v-model="selectedRadioFruit" label="Favorite fruit" :options="radioFruitOptions" />
                            <BaseRadioGroup
                                v-model="selectedRadioAlignment"
                                label="Alignment (horizontal)"
                                orientation="horizontal"
                                :options="alignments.map((a) => ({ value: a.key, label: a.label }))" />
                            <BaseRadioGroup v-model="selectedRadioFruit" label="Disabled group" disabled :options="radioFruitOptions" />
                            <BaseRadioGroup v-model="selectedRadioFruit" label="Card choices" appearance="cards" orientation="horizontal" :options="radioFruitOptions" />
                            <BaseRadioGroup
                                v-model="selectedRadioFruit"
                                label="Disabled card choices"
                                appearance="cards"
                                orientation="horizontal"
                                disabled
                                :options="radioFruitOptions" />
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Scale Slider"
                        keywords="likert rating scale survey"
                        description="A rating scale as a snapping slider. It starts unanswered, follows a drag and settles on the nearest point on release, and names the answer under the track. Use it for Likert and other rating scales.">
                        <div class="flex flex-col gap-10">
                            <BaseScaleSlider v-model="scaleSliderBare" label="Bare scale" :options="scaleSliderOptions" />
                            <BaseScaleSlider v-model="scaleSliderAnswer" label="You prefer to plan ahead." label-start="Disagree" label-end="Agree" :options="scaleSliderOptions" />
                            <BaseScaleSlider :model-value="1" label="Disabled scale" label-start="Disagree" label-end="Agree" disabled :options="scaleSliderOptions" />
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Switch"
                        keywords="toggle"
                        description="A toggle switch component with three sizes (sm, md, lg). Uses reka-ui Switch primitives under the hood.">
                        <div class="flex flex-col gap-4">
                            <div class="flex items-center gap-3">
                                <BaseSwitch v-model="isSwitchSmChecked" size="sm" />
                                <span class="body-small">Small</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <BaseSwitch v-model="isSwitchMdChecked" size="md" />
                                <span class="body-small">Medium</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <BaseSwitch v-model="isSwitchLgChecked" size="lg" />
                                <span class="body-small">Large (default)</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <BaseSwitch v-model="isSwitchDisabledChecked" disabled />
                                <span class="body-small">Disabled</span>
                            </div>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Slider" keywords="range" description="A slider input component with optional start/end labels and aria support.">
                        <BaseSlider v-model="sliderValue" slider-label="Demo slider" :min="0" :max="5" :step="1" label-start="0" label-end="5" />
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Base Calendar"
                        keywords="date picker month"
                        description="Single-date month picker. Week starts Monday. Today is a ring, the selected day is filled. Arrows move, Home/End jump within the week, PageUp/PageDown change month (Shift for year).">
                        <div class="flex flex-wrap items-start gap-6">
                            <div class="flex flex-col gap-2">
                                <BaseCalendar v-model="selectedCalDate" />
                                <span class="body-small text-ink-muted">raised (default)</span>
                            </div>
                            <div class="flex flex-col gap-2">
                                <BaseCalendar v-model="selectedCalDate" surface="outlined" />
                                <span class="body-small text-ink-muted">surface="outlined"</span>
                            </div>
                            <div class="flex flex-col gap-2">
                                <BaseCalendar v-model="selectedCalDate" surface="plain" />
                                <span class="body-small text-ink-muted">surface="plain" (inside a popover or card)</span>
                            </div>
                            <div class="flex flex-col gap-2">
                                <BaseCalendar v-model="selectedCalDate" :min-value="calMinDate" :max-value="calMaxDate" />
                                <span class="body-small text-ink-muted">min-value / max-value (next 30 days)</span>
                            </div>
                        </div>
                        <span><strong>selected:</strong> {{ selectedCalDate?.toString() ?? "none" }}</span>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Document Uploader" keywords="file upload" description="Demo of document upload, which supports pdf, doc and docx files.">
                        <form @submit.prevent="handleUploadFileSubmit">
                            <input ref="fileInput" aria-label="File Upload" type="file" accept=".pdf,.doc,.docx" class="mb-2" @change="handleFileChange" />
                            <BaseButton theme="primary" type="submit" :disabled="!selectedFile"> Upload Document </BaseButton>
                        </form>
                        <BaseNotification v-if="uploadStatus" :theme="uploadStatus.type as BaseNotificationTheme" :text="uploadStatus.message" />
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="File Selection"
                        keywords="file upload dropzone"
                        description="override-document-upload-flag allows file selection when document upload is off. By default, the user flag applies.">
                        <BaseFileUpload
                            v-slot="{ clickInput }"
                            override-document-upload-flag
                            accepted-file-types=".csv"
                            @file-upload="(files) => (selectedCsvName = files[0]?.name ?? '')">
                            <BaseButton theme="neutral" @click="clickInput">Choose CSV file</BaseButton>
                        </BaseFileUpload>
                        <p class="body-small">{{ selectedCsvName || "No file selected" }}</p>
                        <BaseFileUpload v-slot="{ clickInput }" override-document-upload-flag is-disabled>
                            <BaseButton theme="neutral" disabled @click="clickInput">Temporarily disabled</BaseButton>
                        </BaseFileUpload>
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="overlays" title="Overlays" description="Surfaces that float above the page.">
                    <DesignSystemEntry
                        name="Dialog"
                        keywords="modal drawer controlled v-model focus return"
                        description="The default owns a visible title, close control, divider, 24px gutters, a scrolling body with 16px gaps, and an optional #footer for right-aligned actions. Width is narrow (440px), default (640px), or wide (960px); On mobile it becomes a bottom sheet. Open from #trigger or v-model:open; return-focus-to can name the controlled opener.">
                        <div class="flex flex-wrap gap-2">
                            <BaseDialog v-model:open="isDialogShowing" title="Dialog Demo">
                                <template #trigger>
                                    <BaseButton theme="primary">Press me to open dialog</BaseButton>
                                </template>
                                <p class="body-small">Nested tooltips, dropdown menus and comboboxes within dialog work as expected.</p>
                                <BaseTooltip :as-child="false">
                                    <template #trigger>Hover me!</template>
                                    <template #content>Test tooltip</template>
                                </BaseTooltip>
                                <BaseDropdownMenu :sections="dropdownMenuSections" align="center" @select="handleDropdownMenuSelect">
                                    <template #trigger><BaseButton theme="tertiary" class="self-center">Click me!</BaseButton></template>
                                </BaseDropdownMenu>
                                <BaseCombobox v-model="selectedCat" :options="cats" :display-value="(option) => option.name" :key-value="(option) => option.id" />
                                <div v-for="i in 100" :key="i" class="body-small">
                                    Hello I am a very basic dialog with some text content. This text is repeated to demo scrolling in the dialog container.
                                </div>
                                <template #footer><BaseButton theme="primary" @click="isDialogShowing = false">Done</BaseButton></template>
                            </BaseDialog>
                            <BaseButton ref="controlledDialogOpener" theme="neutral" @click="isControlledDialogShowing = true">Open controlled dialog</BaseButton>
                        </div>
                        <BaseDialog v-model:open="isControlledDialogShowing" title="Controlled dialog" width="narrow" :return-focus-to="controlledDialogOpener">
                            <p class="body">Close with Escape or the close button and focus returns to the button that opened this dialog.</p>
                        </BaseDialog>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Expanded mobile dialog"
                        description="Uses up to 90% of the mobile viewport with safe-area padding when no mobile navigation is present.">
                        <BaseDialog title="Expanded dialog" mobile-height="expanded" width="wide" :reserve-mobile-nav-space="false">
                            <template #trigger><BaseButton theme="primary">Open expanded dialog</BaseButton></template>
                            <p class="body">The title and actions stay visible while the body scrolls. This example uses the wide desktop width and expanded mobile height.</p>
                        </BaseDialog>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Coaching dialog" description="Coaching prompts use the same title, spacing and close control as every other dialog.">
                        <BaseDialog title="Anything to share before we begin?">
                            <template #trigger><BaseButton theme="neutral">Open coaching dialog</BaseButton></template>
                            <p class="body text-ink-muted">Share context to help Nadia support you.</p>
                        </BaseDialog>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Custom dialog escape hatch"
                        description="Reserve layout='custom' for full-bleed media, carousels, full-page report previews, and independently composed workflows. Ordinary forms, confirmations and detail views use the standard layout. Custom content keeps an accessible title; show-close and float-close can place a close control over its canvas.">
                        <BaseDialog title="Media preview" layout="custom" show-close float-close width="wide">
                            <template #trigger><BaseButton theme="neutral">Open custom dialog</BaseButton></template>
                            <div class="grid aspect-video place-items-center bg-surface-sunken">
                                <i class="bi bi-play-circle text-4xl text-ink-muted" aria-hidden="true"></i>
                            </div>
                        </BaseDialog>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Confirmation dialog"
                        keywords="modal confirm destructive delete"
                        description="Standard cancel/confirm dialog built on BaseDialog. Pass width='narrow' for the 440px Design System Library modal. actions-justify='between' spreads Cancel and Confirm to the edges.">
                        <div class="flex flex-wrap gap-2">
                            <BaseButton theme="neutral" @click="isConfirmationDialogShowing = true">Default</BaseButton>
                            <BaseButton theme="neutral" @click="isSmallConfirmationDialogShowing = true">width="narrow"</BaseButton>
                        </div>
                        <ConfirmationDialog
                            v-model:open="isConfirmationDialogShowing"
                            title="Delete 2 people?"
                            text="Their notes and context will be permanently removed from Nadia. This can't be undone."
                            confirm-label="Delete (2)"
                            confirm-theme="danger" />
                        <ConfirmationDialog
                            v-model:open="isSmallConfirmationDialogShowing"
                            width="narrow"
                            actions-justify="between"
                            title="Delete 2 people?"
                            text="Their notes and context will be permanently removed from Nadia. This can't be undone."
                            confirm-label="Delete (2)"
                            confirm-theme="danger" />
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Popover"
                        description="Arbitrary floating content, portaled to the body. Pass as-child for a BaseButton trigger; use Dropdown Menu for action lists.">
                        <div class="flex gap-2">
                            <BasePopover as-child>
                                <template #trigger><BaseButton theme="primary">Trigger</BaseButton></template>
                                <template #content>
                                    <p class="body-small p-3">Additional context for this control.</p>
                                </template>
                            </BasePopover>
                            <BasePopover as-child show-arrow :side-offset="6">
                                <template #trigger><BaseButton theme="neutral">With arrow</BaseButton></template>
                                <template #content>
                                    <p class="body-small p-3">Arrow fill and stroke follow the popover tokens.</p>
                                </template>
                            </BasePopover>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Drawer"
                        keywords="sidebar sheet panel"
                        description="An edge-anchored panel, modal by default. side='right' is a bottom sheet on mobile and a full-height right panel from md up; side='bottom' stays a bottom sheet at every viewport. Built on reka's Drawer: swipe toward the anchored edge to close (down for a sheet, right for the desktop panel), plus the same focus trap, Escape, and focus return as BaseDialog. modal='mobile' drops the backdrop from md up so the page behind stays usable (a list beside its detail panel); swipe-to-close=false removes the drag and the sheet handle; dismissible=false holds the drawer open while a save runs; title-voice='plain' with wrap-title shows a regular-weight title that wraps.">
                        <div class="flex flex-wrap gap-2">
                            <BaseButton ref="drawerOpener" theme="primary" @click="isRightDrawerShowing = true">Open right drawer</BaseButton>
                            <BaseButton theme="neutral" @click="isBottomDrawerShowing = true">Open bottom drawer</BaseButton>
                            <BaseButton theme="neutral" @click="isSidePanelDrawerShowing = true">Open side panel (modal="mobile")</BaseButton>
                            <BaseButton theme="neutral" @click="isFullscreenDrawerShowing = true">{{ $t("designSystem.drawer.openFullscreen") }}</BaseButton>
                        </div>
                        <BaseDrawer v-model:open="isRightDrawerShowing" title="Right drawer" :return-focus-to="drawerOpener" :reserve-mobile-nav-space="false">
                            <div class="px-6 py-5 flex flex-col gap-3">
                                <p class="body-small">A full-height right panel on desktop and a bottom sheet on mobile.</p>
                                <BaseTextarea aria-label="Sample field" placeholder="Focus is trapped inside" :rows="2" />
                            </div>
                            <template #footer>
                                <div class="flex justify-end gap-2 px-6 py-4 border-t border-border">
                                    <BaseButton theme="tertiary" @click="isRightDrawerShowing = false">Cancel</BaseButton>
                                    <BaseButton theme="primary" @click="isRightDrawerShowing = false">Save</BaseButton>
                                </div>
                            </template>
                        </BaseDrawer>
                        <BaseDrawer v-model:open="isBottomDrawerShowing" title="Bottom drawer" side="bottom" :reserve-mobile-nav-space="false">
                            <div class="px-6 py-5">
                                <p class="body-small">A bottom sheet at every viewport, for nav-style sheets.</p>
                            </div>
                        </BaseDrawer>
                        <BaseDrawer
                            v-model:open="isSidePanelDrawerShowing"
                            title="A long item title that wraps onto a second line instead of being cut off"
                            title-voice="plain"
                            wrap-title
                            modal="mobile"
                            :swipe-to-close="false"
                            :dismissible="!isSidePanelSaving"
                            :reserve-mobile-nav-space="false">
                            <div class="px-6 py-5 flex flex-col gap-3">
                                <p class="body-small">
                                    From md up there is no backdrop and the page stays clickable, so another item can be picked while this is open. Focus stays in the panel and
                                    Escape closes it. No swipe to close and no sheet handle.
                                </p>
                            </div>
                            <template #footer>
                                <div class="flex justify-end gap-2 px-6 py-4 border-t border-border">
                                    <BaseButton theme="primary" :disabled="isSidePanelSaving" @click="simulateSidePanelSave">
                                        {{ isSidePanelSaving ? "Saving, close is disabled" : "Save" }}
                                    </BaseButton>
                                </div>
                            </template>
                        </BaseDrawer>
                        <BaseDrawer
                            v-model:open="isFullscreenDrawerShowing"
                            :title="$t('designSystem.drawer.fullscreenTitle')"
                            title-voice="serif"
                            mobile-layout="fullscreen"
                            :reserve-mobile-nav-space="false">
                            <div class="px-6 py-5">
                                <p class="body-small">{{ $t("designSystem.drawer.fullscreenDescription") }}</p>
                            </div>
                        </BaseDrawer>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Tooltip" description="Tooltip component is very bare bones, we can style it to whatever shape we want..">
                        <div class="flex flex-wrap gap-4">
                            <BaseTooltip :as-child="false">
                                <template #trigger>Trigger (top)</template>
                                <template #content> This is the tooltip text on the top!</template>
                            </BaseTooltip>
                            <BaseTooltip :as-child="false" side="left">
                                <template #trigger>Trigger (left)</template>
                                <template #content> This is the tooltip text on the left!</template>
                            </BaseTooltip>
                            <BaseTooltip :as-child="false" side="right">
                                <template #trigger>Trigger (right)</template>
                                <template #content> This is the tooltip text on the right!</template>
                            </BaseTooltip>
                            <BaseTooltip :as-child="false" side="bottom">
                                <template #trigger>Trigger (bottom)</template>
                                <template #content> This is the tooltip text on the bottom!</template>
                            </BaseTooltip>
                            <BaseTooltip :as-child="false" theme="dark">
                                <template #trigger>Trigger (dark)</template>
                                <template #content> This is the dark tooltip!</template>
                            </BaseTooltip>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Toast" keywords="snackbar" description="a toast component for displaying brief messages">
                        <div class="flex flex-wrap gap-2">
                            <BaseButton theme="primary" @click="isToastOpen = true">Trigger Toast</BaseButton>
                            <BaseButton theme="primary" @click="isCustomToastOpen = true">Custom content</BaseButton>
                            <BaseButton theme="primary" @click="isErrorToastOpen = true">Error theme</BaseButton>
                            <BaseButton theme="primary" @click="isInfoToastOpen = true">Info theme</BaseButton>
                            <BaseButton theme="primary" @click="isWarningToastOpen = true">Warning theme</BaseButton>
                            <BaseButton theme="primary" @click="isSuccessToastOpen = true">Success theme</BaseButton>
                        </div>
                        <BaseToast
                            v-model:open="isToastOpen"
                            description="This is a toast message. This is a toast message.This is a toast message.This is a toast message.This is a toast message."
                            action-text="I'm an Action">
                        </BaseToast>
                        <BaseToast v-model:open="isCustomToastOpen" description="This is a toast message." action-text="I'm an Action">
                            <template #description><div>🍞 Toast toast toast 🍞</div></template>
                            <template #action>
                                <BaseButton theme="tertiary" size="sm">Close Me</BaseButton>
                            </template>
                        </BaseToast>
                        <BaseToast v-model:open="isErrorToastOpen" theme="error" description="This is a error toast message." action-text="I'm an Action"> </BaseToast>
                        <BaseToast v-model:open="isInfoToastOpen" theme="info" description="This is an informational toast message." action-text="I'm an Action"> </BaseToast>
                        <BaseToast v-model:open="isWarningToastOpen" theme="warning" description="This is a warning toast message." action-text="I'm an Action"> </BaseToast>
                        <BaseToast v-model:open="isSuccessToastOpen" theme="success" description="This is a success toast message." action-text="I'm an Action"> </BaseToast>
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="feedback" title="Feedback and status" description="Telling the person what is happening.">
                    <DesignSystemEntry name="Notification" keywords="alert banner">
                        <BaseNotification theme="primary" text="I am a primary notification" />
                        <BaseNotification theme="error" text="I am an error notification (my naming has been fixed, my identity is restored)" />
                        <BaseNotification theme="info" text="I am an actual info notification" />
                        <BaseNotification theme="warning" text="I am a warning notification" />
                        <BaseNotification theme="success" text="I am a success notification" />
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Count badge"
                        keywords="badge counter tally"
                        description="A small round count. Accent (default) flags something needing attention, ringed when it overlaps an icon; neutral is a plain tally beside a label.">
                        <div class="flex items-center gap-6 body-small text-ink">
                            <span class="flex items-center gap-2">Accent <BaseCountBadge :count="3" :ringed="false" /></span>
                            <span class="flex items-center gap-2">Neutral <BaseCountBadge :count="5" tone="neutral" :ringed="false" /></span>
                            <span class="flex items-center gap-2">Capped <BaseCountBadge :count="120" :ringed="false" /></span>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Progress Bar" description="A progress bar component for indicating progress.">
                        <div class="flex flex-col gap-2">
                            empty / partial / full
                            <BaseProgressBar :value="0" show-label />
                            <BaseProgressBar :value="50" show-label />
                            <BaseProgressBar :value="100" show-label />
                            size "xs"
                            <BaseProgressBar :value="20" size="xs" />
                            <BaseProgressBar :value="50" size="xs" show-label />
                            size "sm"
                            <BaseProgressBar :value="20" size="sm" />
                            <BaseProgressBar :value="50" size="sm" show-label />
                            size "md"
                            <BaseProgressBar :value="20" size="md" />
                            <BaseProgressBar :value="100" size="md" show-label />
                            loading
                            <BaseProgressBar :value="0" is-loading size="md" />
                            <BaseProgressBar :value="0" is-loading size="md" show-label />
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Skeleton Loader"
                        keywords="placeholder shimmer"
                        description="Skeleton loader component is very bare bones, we can style it to whatever shape we want, right now only supports pulsing animation.">
                        <SkeletonLoader class="rounded-full w-12 h-12 mb-2" />
                        <SkeletonLoader class="rounded-md w-full h-4 mb-2" />
                        <SkeletonLoader class="rounded-md w-full h-4 mb-2" />
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Loading Spinner" description="A loading spinner component for indicating loading states.">
                        <LoadingSpinner class="h-10 w-10" />
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Orbiting Particles"
                        description="A GSAP-powered loading animation: gradient dots orbit a glowing center on independent paths. Use as an inline indicator or, with `overlay`, as a full-screen loading state.">
                        <OrbitingParticles />
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="content" title="Content" description="Surfaces and labels that carry information.">
                    <DesignSystemEntry name="Card" description="A surface for grouped content. Theme, padding, radius, and interactive states are configurable.">
                        <div class="flex flex-col gap-3">
                            <BaseCard>Default card · padding md</BaseCard>
                            <BaseCard padding="sm" radius="md">Default · padding sm · radius md</BaseCard>
                            <BaseCard theme="muted">Muted theme (no border)</BaseCard>
                            <BaseCard theme="accent">Accent theme (cyan tinted)</BaseCard>
                            <BaseCard theme="assessment">Assessment theme (pink tinted, for 360s)</BaseCard>
                            <BaseCard interactive>Interactive card (hover to see border shift)</BaseCard>
                            <BaseCard theme="cream" radius="lgr" elevated>Cream theme · radius lgr · elevated (in-chat tool card surface)</BaseCard>
                            <BaseCard theme="cream-muted" radius="lgr">Cream muted theme (quiet cream surface, no border)</BaseCard>
                            <BaseCard
                                as="button"
                                padding="sm"
                                class="flex items-center gap-4 text-left"
                                :selected="isCardSelected"
                                :aria-pressed="isCardSelected"
                                @click="isCardSelected = !isCardSelected">
                                <BaseCheckbox :model-value="isCardSelected" indicator />
                                Selected state · as button with aria-pressed and an indicator checkbox (click to toggle)
                            </BaseCard>
                            <div class="relative rounded-xl border border-border bg-surface-raised p-5">
                                <BaseCard as="button" theme="bare" padding="none" class="text-left before:absolute before:inset-0 before:content-['']">
                                    Bare theme · the stretched button inside a card another element paints
                                </BaseCard>
                            </div>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Insight layout"
                        keywords="insight card reading"
                        description="Use the default card for standalone insights, or plain columns within a page canvas. hideBar omits the decorative rail, for the redesigned My Work insight card treatment.">
                        <div class="grid gap-6 md:grid-cols-2">
                            <InsightCardLayout
                                ><h3 class="nadia-subtitle">A standalone insight</h3>
                                <p class="body-small mt-3">The default card keeps its own surface and decorative rail.</p></InsightCardLayout
                            >
                            <InsightCardLayout appearance="plain"
                                ><h3 class="nadia-subtitle">An open reading column</h3>
                                <p class="body-small mt-3">The plain appearance sits directly on the page canvas.</p></InsightCardLayout
                            >
                            <InsightCardLayout hide-bar
                                ><h3 class="nadia-subtitle">hideBar</h3>
                                <p class="body-small mt-3">No decorative rail.</p></InsightCardLayout
                            >
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="My Work insight card"
                        keywords="mywork insight card redesign homepage feedback"
                        description="The My Work insight card, shared by the My Work page and the homepage carousel. Purely presentational — props configure what shows, and the #feedback slot carries whatever feedback UI the caller wires up (each surface supplies its own).">
                        <div class="flex flex-col gap-6">
                            <MyWorkInsightCard
                                experiment-title="Project Risk Radar"
                                experiment-slug="my_work_insights"
                                title="Your Jennelle 1:1 can give Talent Takeover a direct risk channel"
                                summary="The standup is a shared coordination forum, but Jennelle's preferred way of receiving risk is more decision-ready than a status update."
                                insight-id="design-system-demo"
                                :unknowns="['Would surfacing this a day earlier change how Jennelle prepares?']"
                                :evidence-groups="[]">
                                <template #feedback>
                                    <MyWorkInsightFeedback
                                        variant="buttons"
                                        :feedback-value="myWorkInsightCardFeedbackValue"
                                        :feedback-tags="[]"
                                        :feedback-comment="null"
                                        reason-prompt="never"
                                        @select="myWorkInsightCardFeedbackValue = $event" />
                                </template>
                            </MyWorkInsightCard>
                            <MyWorkInsightCard
                                experiment-title="Coaching Card — Team Tension"
                                experiment-slug="my_work_insights"
                                title="Two Teammates, Two Different Paces"
                                summary="When collaborators on the same project have very different risk tolerances, an insight like this flags the tension early."
                                insight-id="design-system-demo-dismissible"
                                :evidence-groups="[]"
                                show-close
                                @close="() => {}">
                                <template #feedback>
                                    <MyWorkInsightFeedback variant="buttons" :feedback-value="null" :feedback-tags="[]" :feedback-comment="null" reason-prompt="never" />
                                </template>
                            </MyWorkInsightCard>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Chip" keywords="badge tag pill" description="Neutral structural primitive composed into chips with one semantic purpose.">
                        <div class="flex flex-col gap-3">
                            <div class="flex flex-col gap-2">
                                <h4 class="scaffold-body-small-medium text-ink">Sizes</h4>
                                <div class="flex flex-wrap items-center gap-2">
                                    <BaseChip size="2xs">2xs</BaseChip>
                                    <BaseChip size="xs">xs</BaseChip>
                                    <BaseChip>sm</BaseChip>
                                    <BaseChip size="md">md</BaseChip>
                                    <BaseChip size="compact">Compact summary</BaseChip>
                                </div>
                            </div>
                            <div class="flex flex-col gap-2">
                                <h4 class="scaffold-body-small-medium text-ink">Feature chips</h4>
                                <div class="flex flex-wrap gap-2">
                                    <FeatureChip feature="team-reflect">Team Review</FeatureChip>
                                    <FeatureChip feature="self-reflect">Self Reflection</FeatureChip>
                                    <FeatureChip feature="three-sixty">360</FeatureChip>
                                    <FeatureChip feature="journeys">New Journey</FeatureChip>
                                </div>
                            </div>
                            <div class="flex flex-col gap-2">
                                <h4 class="scaffold-body-small-medium text-ink">Insight category chips</h4>
                                <div class="flex flex-wrap gap-2">
                                    <InsightCategoryChip :color="InsightCategoryColor.TEAL">Teal category</InsightCategoryChip>
                                    <InsightCategoryChip :color="InsightCategoryColor.PINK">Pink category</InsightCategoryChip>
                                    <InsightCategoryChip :color="InsightCategoryColor.ORANGE">Orange category</InsightCategoryChip>
                                    <InsightCategoryChip :color="InsightCategoryColor.GREEN">Green category</InsightCategoryChip>
                                </div>
                            </div>
                            <div class="flex flex-col gap-2">
                                <h4 class="scaffold-body-small-medium text-ink">Source chips</h4>
                                <div class="flex flex-wrap gap-2">
                                    <SourceChip source="nadia">Suggested by Nadia</SourceChip>
                                    <SourceChip source="three-sixty">From 360</SourceChip>
                                    <SourceChip source="self-reflect">From engagement survey</SourceChip>
                                    <SourceChip>From performance conversations</SourceChip>
                                </div>
                            </div>
                            <div class="flex flex-col gap-2">
                                <h4 class="scaffold-body-small-medium text-ink">Status chips</h4>
                                <div class="flex flex-wrap gap-2">
                                    <StatusChip state="active">Active</StatusChip>
                                    <StatusChip state="done">1 of 2 done</StatusChip>
                                    <StatusChip state="pending">Pending</StatusChip>
                                    <StatusChip state="overdue">Overdue</StatusChip>
                                    <StatusChip>No status</StatusChip>
                                </div>
                            </div>
                            <div class="flex flex-col gap-2">
                                <h4 class="scaffold-body-small-medium text-ink">Capability chips</h4>
                                <div class="flex flex-wrap gap-2">
                                    <CapabilityChip>Coach and develop your teammates</CapabilityChip>
                                </div>
                            </div>
                            <div class="flex flex-col gap-2">
                                <h4 class="scaffold-body-small-medium text-ink">Type chips</h4>
                                <div class="flex flex-wrap gap-2">
                                    <BaseChip size="2xs">
                                        <template #leading><i class="bi bi-key" aria-hidden="true"></i></template>
                                        Key meeting
                                    </BaseChip>
                                </div>
                            </div>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Avatar"
                        keywords="initials people calendar perspective profile"
                        description="BaseAvatar owns geometry, default typography, no-translate and decorative accessibility. Semantic compositions own identity, colour and typography.">
                        <div class="flex flex-col gap-4">
                            <div>
                                <p class="body-small-semibold mb-2">Primitive: default, sizes, shape and disabled</p>
                                <div class="flex flex-wrap items-center gap-2">
                                    <BaseAvatar />
                                    <BaseAvatar size="2xs" initials="AB" />
                                    <BaseAvatar size="xs" initials="AB" />
                                    <BaseAvatar size="sm" initials="AB" />
                                    <BaseAvatar initials="AB" />
                                    <BaseAvatar size="lg" initials="AB" />
                                    <BaseAvatar size="xl" initials="AB" />
                                    <BaseAvatar shape="square"><i class="bi bi-stars" aria-hidden="true" /></BaseAvatar>
                                    <BaseAvatar initials="AB" disabled />
                                </div>
                            </div>
                            <div>
                                <p class="body-small-semibold mb-2">PeopleAvatar: 45px default, 40px chat rows, 32px dense rows</p>
                                <div class="flex flex-wrap items-start gap-4">
                                    <span class="ds-avatar-demo">
                                        <PeopleAvatar initials="G" letters="ESFJ" />
                                        <code class="ds-avatar-caption">default</code>
                                    </span>
                                    <span class="ds-avatar-demo">
                                        <PeopleAvatar initials="TM" letters="ESFJ" />
                                        <code class="ds-avatar-caption">two initials</code>
                                    </span>
                                    <span class="ds-avatar-demo">
                                        <PeopleAvatar initials="G" disabled />
                                        <code class="ds-avatar-caption">disabled · no type known</code>
                                    </span>
                                    <span class="ds-avatar-demo">
                                        <PeopleAvatar size="sm" initials="G" letters="ESFJ" />
                                        <code class="ds-avatar-caption">40px · chat rows</code>
                                    </span>
                                    <span class="ds-avatar-demo">
                                        <PeopleAvatar size="xs" initials="GM" letters="ESFJ" />
                                        <code class="ds-avatar-caption">32px · dense rows</code>
                                    </span>
                                </div>
                                <p class="body-small-semibold mt-4 mb-2">The sixteen hues: flat swatches, initial contrast per hue (⚠ under 4.5:1)</p>
                                <div class="flex flex-wrap items-start gap-4">
                                    <span v-for="hue in peopleAvatarHues" :key="hue.letters" class="ds-avatar-demo">
                                        <PeopleAvatar :initials="hue.name.charAt(0)" :letters="hue.letters" />
                                        <code class="ds-avatar-caption"
                                            >{{ hue.name }} · {{ formatNumber(hue.contrast, "two-decimals") }}:1{{ hue.contrast < 4.5 ? " ⚠" : "" }}</code
                                        >
                                    </span>
                                </div>
                                <p class="body-small-semibold mt-4 mb-2">In an identity row</p>
                                <div class="flex flex-wrap items-start gap-4">
                                    <span v-for="person in peopleAvatarIdentityRows" :key="person.name" class="ds-avatar-demo">
                                        <span class="flex items-start gap-2.5">
                                            <PeopleAvatar :name="person.name" :initials="person.name.charAt(0)" :letters="person.letters" :disabled="person.disabled" />
                                            <span class="flex min-w-0 flex-col">
                                                <span class="nadia-title-subsection text-ink">{{ person.name }}</span>
                                                <span class="body-small text-ink-muted">{{ person.role }}</span>
                                            </span>
                                        </span>
                                        <code class="ds-avatar-caption">{{ person.disabled ? "disabled" : "default" }}</code>
                                    </span>
                                </div>
                            </div>
                            <div>
                                <p class="body-small-semibold mb-2">CollaborationProfileAvatar: availability and compact initials</p>
                                <div class="flex flex-wrap items-center gap-2">
                                    <CollaborationProfileAvatar name="Grace MacNamara" />
                                    <CollaborationProfileAvatar name="Grace MacNamara" :available="false" />
                                    <CollaborationProfileAvatar name="Wendy Williams" size="xs" />
                                    <CollaborationProfileAvatar name="Wendy Williams" size="sm" />
                                </div>
                            </div>
                            <div>
                                <p class="body-small-semibold mb-2">CalendarAttendeeAvatar: name, email and unknown attendee</p>
                                <div class="flex flex-wrap items-center gap-2">
                                    <CalendarAttendeeAvatar name="Grace MacNamara" />
                                    <CalendarAttendeeAvatar email="grace@example.com" />
                                    <CalendarAttendeeAvatar />
                                </div>
                            </div>
                            <div>
                                <p class="body-small-semibold mb-2">PerspectiveAvatar: actual type colour or unknown type</p>
                                <div class="flex flex-wrap items-center gap-2">
                                    <PerspectiveAvatar v-for="letters in PEOPLE_AVATAR_HUE_ORDER" :key="letters" name="Grace MacNamara" :letters="letters" />
                                    <PerspectiveAvatar name="Grace MacNamara" />
                                </div>
                            </div>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Date"
                        keywords="time datetime relative locale i18n"
                        description="A date, time or relative time in a <time> element. Pick a format; the active UI language decides word order, separators and hour cycle. Date-only values are calendar days and never shift by zone.">
                        <div class="flex flex-col gap-2 body-small text-ink">
                            <p>Default (date): <BaseDate :value="demoInstant" /></p>
                            <p>date-long: <BaseDate :value="demoInstant" format="date-long" /></p>
                            <p>month-day: <BaseDate :value="demoInstant" format="month-day" /></p>
                            <p>month-day-long: <BaseDate :value="demoInstant" format="month-day-long" /></p>
                            <p>month: <BaseDate :value="demoInstant" format="month" /></p>
                            <p>month-year: <BaseDate :value="demoInstant" format="month-year" /></p>
                            <p>weekday: <BaseDate :value="demoInstant" format="weekday" /></p>
                            <p>time: <BaseDate :value="demoInstant" format="time" /></p>
                            <p>datetime: <BaseDate :value="demoInstant" format="datetime" /></p>
                            <p>relative, with the absolute date in hidden text: <BaseDate :value="demoThreeDaysAgo" format="relative" /></p>
                            <p>Calendar day: <BaseDate value="2026-01-04" format="date-long" /></p>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Number"
                        description="formatNumber(value, format) for every number a user reads. Pick a format; the active UI language decides the decimal and grouping separators. Percent formats take a fraction (0.35).">
                        <div class="flex flex-col gap-2 body-small text-ink">
                            <p>Default (decimal): {{ formatNumber(1234.5) }}</p>
                            <p>one-decimal: {{ formatNumber(4, "one-decimal") }}</p>
                            <p>two-decimals: {{ formatNumber(3.5, "two-decimals") }}</p>
                            <p>up-to-one-decimal: {{ formatNumber(4, "up-to-one-decimal") }} and {{ formatNumber(3.45, "up-to-one-decimal") }}</p>
                            <p>percent: {{ formatNumber(0.35, "percent") }}</p>
                            <p>signed-percent: {{ formatNumber(0.05, "signed-percent") }} and {{ formatNumber(-0.12, "signed-percent") }}</p>
                            <p>compact: {{ formatNumber(15300, "compact") }}</p>
                        </div>
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="disclosure" title="Navigation and disclosure" description="Showing one slice of content at a time.">
                    <DesignSystemEntry
                        name="Tabs"
                        description="Tabs with a plain-text label per tab (no icons) and a content slot per tab. Supports horizontal (default) and vertical orientation. A step row whose tabs need an icon, date or state (the Moments timeline) draws each tab through the #tab slot; BaseTabs keeps the roles, keyboard and selection.">
                        <BaseTabs :tab-ids="tabIds" :labels="tabLabels">
                            <template v-for="id in tabIds" :key="id" #[`content-${id}`]> Content {{ id }} </template>
                        </BaseTabs>
                        <div class="mt-4">
                            <p class="body-small mb-2">Vertical</p>
                            <BaseTabs :tab-ids="tabIds" :labels="tabLabels" orientation="vertical">
                                <template v-for="id in tabIds" :key="id" #[`content-${id}`]> Content {{ id }} </template>
                            </BaseTabs>
                        </div>
                        <div class="mt-4">
                            <p class="body-small mb-2">With a count</p>
                            <BaseTabs :tab-ids="tabIds" :labels="tabLabels" :counts="{ '3': 5 }">
                                <template v-for="id in tabIds" :key="id" #[`content-${id}`]> Content {{ id }} </template>
                            </BaseTabs>
                        </div>
                        <div class="mt-4">
                            <p class="body-small mb-2">Disabled</p>
                            <BaseTabs :tab-ids="tabIds" :labels="tabLabels" disabled>
                                <template v-for="id in tabIds" :key="id" #[`content-${id}`]> Content {{ id }} </template>
                            </BaseTabs>
                        </div>
                        <div class="mt-4">
                            <p class="body-small mb-2">Slot-drawn steps, with one disabled (disabled-tab-ids)</p>
                            <BaseTabs :tab-ids="tabIds" :labels="tabLabels" :disabled-tab-ids="['3']">
                                <template #tab="{ tabId, selected, disabled: isStepDisabled }">
                                    <div class="flex flex-col gap-1 py-3" :class="isStepDisabled ? 'text-ink-muted' : 'text-ink'">
                                        <span :class="selected ? 'body-small-bold' : 'body-small'">{{ tabLabels[tabId as keyof typeof tabLabels] }}</span>
                                        <span class="details text-ink-muted">{{ isStepDisabled ? "Not open yet" : "Step " + tabId }}</span>
                                    </div>
                                    <div class="absolute bottom-0 inset-x-0 h-1 rounded-full" :class="selected ? 'bg-tabs-indicator' : 'bg-tabs-hairline'"></div>
                                </template>
                                <template v-for="id in tabIds" :key="id" #[`content-${id}`]> Content {{ id }} </template>
                            </BaseTabs>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Accordion"
                        description="This is the most bare bones Accordion, but its easy enought to extend into a custom version (ie. our nadia notes accordions!)">
                        <BaseAccordion :accordion-ids="accordionIds">
                            <template v-for="id in accordionIds" :key="id" #[`trigger-${id}`]> Trigger {{ id }} </template>
                            <template v-for="id in accordionIds" :key="id" #[`content-${id}`]> Content {{ id }} </template>
                        </BaseAccordion>
                        <p class="body-small mt-4 mb-2">With <code>default-value</code>, expanded on first render:</p>
                        <BaseAccordion :accordion-ids="accordionIds" :default-value="[accordionIds[1]]">
                            <template v-for="id in accordionIds" :key="id" #[`trigger-${id}`]> Trigger {{ id }} </template>
                            <template v-for="id in accordionIds" :key="id" #[`content-${id}`]> Content {{ id }} </template>
                        </BaseAccordion>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Collapsible Section"
                        keywords="expand disclosure"
                        description="This is a wrapper component that can take a custom trigger and display custom content. The expand/collapse is animated and it is fully a11y compliant (screen reader + keyboard).">
                        <BaseCollapsible>
                            <template #trigger> Trigger </template>
                            <template #content> This is just a bunch of random text content I put in to show that it can expand.</template>
                        </BaseCollapsible>

                        <BaseButton theme="primary" class="mt-4" @click="isCollapsibleOpen = !isCollapsibleOpen">
                            Controlled {{ isCollapsibleOpen ? "Collapse" : "Expand" }} Collapsible
                        </BaseButton>

                        <BaseCollapsible v-model:open="isCollapsibleOpen">
                            <template #content> This is just a bunch of random text content I put in to show that it can expand using the controlled component.</template>
                        </BaseCollapsible>

                        <BaseCollapsible stretched-trigger trigger-aria-label="Toggle event details" class="mt-4 rounded-lgr">
                            <template #trigger>
                                <div class="pointer-events-none flex items-center justify-between gap-4 rounded-lgr border border-border p-4">
                                    <span class="body">The whole row toggles this collapsible.</span>
                                    <BaseButton class="relative pointer-events-auto" theme="neutral" size="sm" @click="isStretchedActionActive = !isStretchedActionActive">
                                        Independent action: {{ isStretchedActionActive ? "on" : "off" }}
                                    </BaseButton>
                                </div>
                            </template>
                            <template #content>
                                <div class="p-4">The action remains separate from the stretched trigger.</div>
                            </template>
                        </BaseCollapsible>

                        <BaseCard padding="none" class="mt-4 overflow-hidden">
                            <BaseCollapsible row-trigger>
                                <template #trigger>
                                    <div class="flex w-full items-center justify-between gap-4 p-5">
                                        <span class="body-bold">Row trigger: the whole card header, with a hover tint</span>
                                        <i class="bi bi-chevron-down text-ink-muted" aria-hidden="true"></i>
                                    </div>
                                </template>
                                <template #content>
                                    <div class="px-5 pb-5 body-small">For a card header with no buttons of its own.</div>
                                </template>
                            </BaseCollapsible>
                        </BaseCard>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Carousel" keywords="slider slideshow" description="a carousel component for displaying a series of content items">
                        <BaseCarousel :item-ids="['1', '2', '3']">
                            <template v-for="id in [1, 2, 3]" :key="id" #[`item-${id}`]>
                                <div>Item {{ id }}</div>
                            </template>
                        </BaseCarousel>
                        <BaseCarousel :item-ids="['1', '2', '3']" label="Example insights" item-label="insight" animated show-count>
                            <template v-for="id in [1, 2, 3]" :key="id" #[`item-${id}`]>
                                <BaseCard
                                    ><p class="body-small">Insight {{ id }} · horizontal transition with progress</p></BaseCard
                                >
                            </template>
                        </BaseCarousel>
                        <BaseCarousel :item-ids="['1', '2', '3']" :items-per-view="2" label="Team suggestions" item-label="recommendation" animated show-count>
                            <template #count="{ start, end, total }">{{ start }}–{{ end }} of {{ total }}</template>
                            <template v-for="id in [1, 2, 3]" :key="id" #[`item-${id}`]>
                                <BaseCard
                                    ><p class="body-small">Recommendation {{ id }}</p></BaseCard
                                >
                            </template>
                        </BaseCarousel>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Section navigation"
                        keywords="section nav in-page navigation"
                        description="Compact in-page navigation: horizontal on mobile, a card of section links on desktop. Uses neutral navigation tokens.">
                        <div class="max-w-48">
                            <BaseSectionNav
                                label="Example sections"
                                model-value="you"
                                :items="[
                                    { id: 'you', label: 'About you', icon: 'bi-person', completed: true },
                                    { id: 'calendar', label: 'Calendar', icon: 'bi-calendar-week' },
                                    { id: 'profile', label: 'Collaboration profile', disabled: true, note: 'Complete Perspectives first' },
                                ]" />
                        </div>
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="page-structure" title="Page structure" description="The wrappers every user-facing page is built from.">
                    <DesignSystemEntry
                        name="Horizontal Divider"
                        keywords="HorizontalDivider separator thematic break hr decorative spacing"
                        description="A full-width rule using border-border in both themes. The default div is decorative with 16px vertical margins. Use as=hr for a native thematic break; role=presentation keeps an hr decorative. Use spacing=none when the surrounding layout owns the gap. Root utilities can preserve one-off margins, responsive visibility or a surface-specific border token.">
                        <div>
                            <p class="body-small">Default decorative divider</p>
                            <HorizontalDivider />
                            <p class="body-small">Native thematic break: as="hr"</p>
                            <HorizontalDivider as="hr" />
                            <p class="body-small">Decorative native rule: as="hr" role="presentation"</p>
                            <HorizontalDivider as="hr" role="presentation" />
                            <div class="flex flex-col gap-3">
                                <p class="body-small">Parent-owned spacing: spacing="none"</p>
                                <HorizontalDivider as="hr" spacing="none" />
                                <p class="body-small">The surrounding layout supplies the gap.</p>
                            </div>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Layout"
                        wide
                        keywords="page wrapper gutters"
                        description="Standard page wrapper for user-facing pages: page background (bg-surface), responsive gutters, and a centered content column that is always a flex column with standard top padding. Configure it with props — width (narrow / default / full), gap (none / sm / md / lg / xl), fill-height, no-top-padding — and reach for content-class only for genuine one-offs. Optional #header slot renders above the column, aligned with the gutters; pass full-bleed-header for edge-to-edge sticky bars (e.g. report action bars).">
                        <div class="flex flex-col gap-6">
                            <BaseLayout class="rounded-lg border border-dashed border-border-strong">
                                <template #header>
                                    <div class="px-6 pt-6 md:px-0 body-small text-ink-muted">#header slot — above the content column, inside the page gutters</div>
                                </template>
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">Default: centered max-w-225 column, gap-6 between sections</div>
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">Second section — spacing comes from the default gap</div>
                            </BaseLayout>
                            <BaseLayout width="narrow" gap="sm" class="rounded-lg border border-dashed border-border-strong">
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">width="narrow" — reading column for reports and results</div>
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">gap="sm" — tighter rhythm for list-style pages</div>
                            </BaseLayout>
                            <BaseLayout width="full" gap="lg" class="rounded-lg border border-dashed border-border-strong">
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">width="full" — boards, wide tables, dashboards</div>
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">gap="lg" — roomier rhythm for section-heavy pages</div>
                            </BaseLayout>
                            <BaseLayout gap="none" content-class="items-center" class="rounded-lg border border-dashed border-border-strong">
                                <div class="rounded-lg border border-border bg-surface-raised p-5 body">
                                    content-class="items-center" — escape hatch for one-off layout. Used twice? Make it a variant.
                                </div>
                            </BaseLayout>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Page Header"
                        wide
                        description="Standard header for user-facing pages: a 20px margin above and below, optional #eyebrow (back link / chip above the title, fixed 12px gap), title, subtitle (full width under the title row with subtitle-below), separator, and an #actions CTA slot. Page top padding belongs to BaseLayout's content column, so the header spaces itself identically whether or not a back link or banner renders above it. Space below the header is the 20px margin plus the page's content gap.">
                        <BasePageHeader
                            title="Page Header"
                            subtitle="A subtitle providing additional context. The separator below spans the full row."
                            class="rounded-lg border border-dashed border-border-strong p-4">
                            <template #eyebrow>
                                <AssessmentEyebrow>Eyebrow slot</AssessmentEyebrow>
                            </template>
                            <template #actions> <BaseButton theme="secondary" size="sm" class="self-start shrink-0">Call to action</BaseButton> </template>
                        </BasePageHeader>
                        <BasePageHeader variant="section" title="Section heading" subtitle="A smaller system heading for a workspace within a page." no-border>
                            <template #eyebrow><p>Section eyebrow</p></template>
                        </BasePageHeader>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Page Title"
                        wide
                        description="The title row used inside BasePageHeader — reach for it directly only in embedded contexts (cards, dialogs, wizards). Sets the visible heading (display-serif: 24px on mobile, 30px from md, --color-ink), optional subtitle (body-small: Inter 14px/20px, --color-page-title-subtitle, 8px below the title, inline or full width), and a bottom border separator in --color-border (opt out with no-border). The #actions slot renders a CTA to the right of the title on desktop, stacked below on mobile — its content is a direct flex child (deliberately, so a responsive-hidden CTA leaves no phantom gap), so give it self-start shrink-0 and wrap multiple actions in one element. Pass subtitle-below when the actions leave the text column too narrow for the sentence: the subtitle then gets its own full-width row under the title, wrapped in a div rather than a p so the slot may hold its own inline CTA. Pass subtitle-multiline for admin-authored subcopy, where newlines the author typed should render as breaks. Use Inertia's Head separately in each page for browser tab titles.">
                        <div class="flex flex-col gap-6">
                            <BasePageTitle title="Example Page Title" />
                            <BasePageTitle title="Page Title with Subtitle" subtitle="This is a subtitle that provides additional context about the page." />
                            <BasePageTitle title="Page Title with CTA" subtitle="A button or other call to action passed via the #actions slot appears to the right.">
                                <template #actions>
                                    <BaseButton theme="secondary" size="sm" class="self-start shrink-0">Call to action</BaseButton>
                                </template>
                            </BasePageTitle>
                            <BasePageTitle title="Page Title with Multiline Subtitle" :subtitle="multilineSubtitle" subtitle-multiline />
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry
                        name="Header"
                        keywords="heading"
                        description="Heading styles from the typography utilities. Defaults to m/system (title-subsection). XL uses display-serif or display-sans-serif, responsive from 24px to 30px at md. M uses display-small-serif or title-subsection. S uses nadia-title-subsection or title-body-bold. Sizes retain their h2, h4, and h5 tags.">
                        <BaseHeader class="mb-6">Default system heading</BaseHeader>
                        <div class="flex flex-wrap gap-4 mb-4">
                            <BaseSelect v-model="selectedHeaderSize" :options="headerSizeOptions" aria-label="Heading size" />
                            <BaseSelect v-model="selectedHeaderUsage" :options="headerUsageOptions" aria-label="Heading voice" />
                        </div>
                        <BaseHeader :size="selectedHeaderSize" :usage="selectedHeaderUsage">Heading over one or more lines</BaseHeader>
                    </DesignSystemEntry>
                </DesignSystemSection>

                <DesignSystemSection id="foundations" title="Foundations" description="Type and elevation the components are built on.">
                    <DesignSystemEntry
                        name="Typography"
                        wide
                        keywords="type fonts text styles"
                        description="Typography styles that are used in our design system. Grouped by family: Display (hero), Title (sans), Body (sans), Nadia (Mackinac serif), Scaffold (Space Grotesk), Details (small sans).">
                        <div class="grid grid-cols-1 gap-10 md:grid-cols-2">
                            <section v-for="family in typographyFamilies" :key="family.label">
                                <h4 class="body-small uppercase text-ink-muted">{{ family.label }}</h4>
                                <HorizontalDivider class="mt-0" />
                                <div class="flex flex-col gap-3">
                                    <div v-for="style in family.styles" :key="style">
                                        <div class="scaffold-details-small text-ink-muted">.{{ style }}</div>
                                        <div :class="style">{{ toStyleLabel(style) }}</div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </DesignSystemEntry>
                    <DesignSystemEntry name="Shadows" keywords="elevation box shadow" description="our different box shadow">
                        <div class="flex gap-4">
                            <div class="w-full grid place-content-center h-10 shadow-xs">xs</div>
                            <div class="w-full grid place-content-center h-10 shadow-sm">sm</div>
                            <div class="w-full grid place-content-center h-10 shadow-md">md</div>
                            <div class="w-full grid place-content-center h-10 shadow-lg">lg</div>
                            <div class="w-full grid place-content-center h-10 shadow-xl">xl</div>
                        </div>
                    </DesignSystemEntry>
                </DesignSystemSection>
            </div>

            <nav aria-label="Design system sections" class="ds-rail">
                <p class="scaffold-details-small uppercase text-ink-muted">On this page</p>
                <a
                    v-for="section in visibleSections"
                    :key="section.id"
                    :href="`#${section.id}`"
                    class="ds-rail-link"
                    :class="activeSectionId === section.id && 'is-active'"
                    :aria-current="activeSectionId === section.id ? 'location' : undefined">
                    <span>{{ section.title }}</span>
                    <span class="scaffold-details-small text-ink-muted">{{ section.visible }}</span>
                </a>
            </nav>
        </div>
    </div>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { getCookie } from "/js/utils";
import { Head } from "@inertiajs/vue3";
import { type DateValue, getLocalTimeZone, today } from "@internationalized/date";
import { useElementSize } from "@vueuse/core";
import AssessmentEyebrow from "~vue/components/AssessmentEyebrow.vue";
import BaseAccordion from "~vue/components/BaseAccordion.vue";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton, { type ButtonTheme } from "~vue/components/BaseButton.vue";
import BaseCalendar from "~vue/components/BaseCalendar.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseCarousel from "~vue/components/BaseCarousel.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseCollapsible from "~vue/components/BaseCollapsible.vue";
import BaseCombobox, { type ComboboxAction } from "~vue/components/BaseCombobox.vue";
import BaseCountBadge from "~vue/components/BaseCountBadge.vue";
import BaseDate from "~vue/components/BaseDate.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseDrawer from "~vue/components/BaseDrawer.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseFileUpload from "~vue/components/BaseFileUpload.vue";
import BaseHeader, { type HeaderSize, type HeaderUsage } from "~vue/components/BaseHeader.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification, { type BaseNotificationTheme } from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import BaseSectionNav from "~vue/components/BaseSectionNav.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import BaseSwitch from "~vue/components/BaseSwitch.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import BaseToggleGroup from "~vue/components/BaseToggleGroup.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import CalendarAttendeeAvatar from "~vue/components/CalendarAttendeeAvatar.vue";
import CapabilityChip from "~vue/components/CapabilityChip.vue";
import CollaborationProfileAvatar from "~vue/components/CollaborationProfileAvatar.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import FeatureChip from "~vue/components/FeatureChip.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import BaseCheckboxGroup from "~vue/components/form/BaseCheckboxGroup.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import BaseRadioGroup from "~vue/components/form/BaseRadioGroup.vue";
import BaseScaleSlider from "~vue/components/form/BaseScaleSlider.vue";
import BaseSlider from "~vue/components/form/BaseSlider.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import FormField from "~vue/components/form/FormField.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import InsightCardLayout from "~vue/components/InsightCardLayout.vue";
import InsightCategoryChip from "~vue/components/InsightCategoryChip.vue";
import MyWorkInsightCard from "~vue/components/MyWorkInsightCard.vue";
import OrbitingParticles from "~vue/components/OrbitingParticles.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import PerspectiveAvatar from "~vue/components/PerspectiveAvatar.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import SourceChip from "~vue/components/SourceChip.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { InsightCategoryColor } from "~vue/Labs/insightCategory";
import MyWorkInsightFeedback from "~vue/Labs/MyWorkInsightFeedback.vue";
import { PERSPECTIVE_TYPES } from "~vue/pages/perspectiveTypes";
import BaseLink from "~vue/router/BaseLink.vue";
import { formatNumber } from "~vue/utils/formatNumber";
import { getPerspectiveSwatch } from "~vue/utils/perspectivePalette";
import { withRequestId } from "~vue/utils/requestId";
import { DateTime } from "luxon";
import { computed, onBeforeUnmount, onMounted, provide, ref, shallowReactive, shallowRef, useTemplateRef, watch } from "vue";

import { designSystemPageKey, designSystemQueryKey, type RegisteredSection } from "./designSystem/context";
import DesignSystemEntry from "./designSystem/DesignSystemEntry.vue";
import DesignSystemSection from "./designSystem/DesignSystemSection.vue";

// This is an internal only page to showcase some of the design system related assets and components we have

const query = ref("");
const demoInstant = DateTime.now().toISO() ?? "";
const demoThreeDaysAgo = DateTime.now().minus({ days: 3 }).toISO() ?? "";
provide(designSystemQueryKey, query);

const sections = shallowReactive<RegisteredSection[]>([]);
provide(designSystemPageKey, (section: RegisteredSection) => sections.push(section));

const visibleSections = computed(() =>
    sections.filter((section) => section.visible.value > 0).map((section) => ({ id: section.id, title: section.title, visible: section.visible.value })),
);

const entryTotals = computed(() =>
    sections.reduce((totals, section) => ({ visible: totals.visible + section.visible.value, total: totals.total + section.total.value }), { visible: 0, total: 0 }),
);

const filterStatus = computed(() =>
    query.value.trim()
        ? `${entryTotals.value.visible} of ${entryTotals.value.total} components match “${query.value.trim()}”.`
        : `Showing all ${entryTotals.value.total} components.`,
);

const filterShortcutHint = globalThis.navigator?.platform?.startsWith("Mac") ? "⌘K" : "Ctrl+K";

const activeSectionId = ref("");
let sectionObserver: IntersectionObserver | undefined;

const searchWrapper = useTemplateRef<HTMLElement>("searchWrapper");
const toolbar = useTemplateRef<HTMLElement>("toolbar");
const { height: toolbarHeight } = useElementSize(toolbar, { width: 0, height: 0 }, { box: "border-box" });

watch(
    toolbarHeight,
    (height, previousHeight) => {
        if (height <= previousHeight) {
            return;
        }
        const target = globalThis.document.getElementById(globalThis.location.hash.slice(1));
        const top = target?.getBoundingClientRect().top;
        // Adding the mobile pill row can cover an anchor that was already visible.
        if (top !== undefined && top >= 0 && top < (toolbar.value?.getBoundingClientRect().bottom ?? 0)) {
            target?.scrollIntoView({ block: "start" });
        }
    },
    { flush: "post" },
);

// A modifier is required rather than a bare "/": a single-character shortcut with no way to disable,
// remap or scope it fails WCAG 2.1.4 for speech-input and keyboard users.
const focusSearchShortcut = (event: KeyboardEvent) => {
    if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey) || event.altKey) {
        return;
    }
    event.preventDefault();
    searchWrapper.value?.querySelector("input")?.focus();
};

onMounted(() => {
    globalThis.document.addEventListener("keydown", focusSearchShortcut);
    sectionObserver = new IntersectionObserver(
        (entries) => {
            const topMost = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
            if (topMost) {
                activeSectionId.value = topMost.target.id;
            }
        },
        { rootMargin: "-140px 0px -55% 0px" },
    );
    for (const section of sections) {
        const element = globalThis.document.getElementById(section.id);
        if (element) {
            sectionObserver.observe(element);
        }
    }
});

onBeforeUnmount(() => {
    globalThis.document.removeEventListener("keydown", focusSearchShortcut);
    sectionObserver?.disconnect();
});

const typographyFamilies = [
    { label: "Display", styles: ["display-medium", "display-serif", "display-sans-serif", "display-small-serif"] },
    { label: "Title", styles: ["title", "title-section", "title-subsection", "title-body", "title-body-bold", "title-small"] },
    { label: "Body", styles: ["body-light", "body", "body-semibold", "body-bold", "body-small", "body-small-semibold", "body-small-bold", "body-serif"] },
    {
        label: "Nadia (Mackinac serif)",
        styles: ["nadia-chat", "nadia-chat-semibold", "nadia-chat-bold", "nadia-title-subsection", "nadia-subtitle", "nadia-subtitle-small"],
    },
    {
        label: "Scaffold (Space Grotesk)",
        styles: ["scaffold-body", "scaffold-body-bold", "scaffold-body-small", "scaffold-body-small-medium", "scaffold-details-bold", "scaffold-details-small"],
    },
    { label: "Details", styles: ["details", "details-big"] },
];

const toStyleLabel = (style: string) =>
    style
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

const isDialogShowing = ref(false);
const myWorkInsightCardFeedbackValue = ref<0 | 1 | null>(null);
const isControlledDialogShowing = ref(false);
const isConfirmationDialogShowing = ref(false);
const isSmallConfirmationDialogShowing = ref(false);
const controlledDialogOpener = useTemplateRef("controlledDialogOpener");
const isRightDrawerShowing = ref(false);
const isBottomDrawerShowing = ref(false);
const isFullscreenDrawerShowing = ref(false);
const isSidePanelDrawerShowing = ref(false);
const isSidePanelSaving = ref(false);
const simulateSidePanelSave = () => {
    isSidePanelSaving.value = true;
    globalThis.setTimeout(() => {
        isSidePanelSaving.value = false;
    }, 2000);
};
const drawerOpener = useTemplateRef("drawerOpener");

const accordionIds = ["1", "2", "3"];

const multilineSubtitle = "The author typed a newline after this sentence.\nSo this renders on its own line.";

const tabIds = ["1", "2", "3"];
const tabLabels = { "1": "Tab 1", "2": "Tab 2", "3": "Tab 3" };

const buttonThemeOptions = [
    { key: "primary", label: "Primary" },
    { key: "secondary", label: "Secondary" },
    { key: "tertiary", label: "Tertiary" },
    { key: "neutral", label: "Neutral" },
    { key: "cream", label: "Cream" },
    { key: "danger", label: "Danger" },
    { key: "ghost", label: "Ghost (icon action)" },
];
const selectedButtonTheme = ref<ButtonTheme>("primary");
const designSystemSearch = ref("");

// Document Uploader
const selectedCsvName = ref("");
const selectedFile = ref<{ file: File } | null>(null);
const uploadStatus = ref<{ type: string; message: string } | null>(null);

const handleFileChange = async (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) {
        return;
    }

    selectedFile.value = { file };
};

const fileInput = ref<HTMLInputElement | null>(null);

const uploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch("/api/chat/document/upload", {
        method: "POST",
        body: formData,
        headers: withRequestId({
            "X-CSRFToken": getCookie("csrftoken") ?? "",
        }),
    });
    return response.json();
};

const handleUploadFileSubmit = async () => {
    if (!selectedFile.value) {
        return;
    }
    try {
        const data = await uploadFile(selectedFile.value.file);
        uploadStatus.value = {
            type: "info",
            message: `File uploaded successfully! File id: ${data.id} (this id should be used to link the file to the chat message)`,
        };
        selectedFile.value = null;
        if (fileInput.value) {
            fileInput.value.value = "";
        }
    } catch (err) {
        uploadStatus.value = {
            type: "secondary",
            message: err instanceof Error ? err.message : String(err),
        };
    }
};

const headerSizeOptions: { key: HeaderSize; label: string }[] = [
    { key: "xl", label: "XL (h2)" },
    { key: "m", label: "M (h4)" },
    { key: "s", label: "S (h5)" },
];
const headerUsageOptions: { key: HeaderUsage; label: string }[] = [
    { key: "nadia", label: "Nadia" },
    { key: "system", label: "System" },
];
const selectedHeaderSize = ref<HeaderSize>("m");
const selectedHeaderUsage = ref<HeaderUsage>("system");

const dropdownItems = [
    { key: "option1", label: "Option 1" },
    { key: "option2", label: "Option 2" },
    { key: "option3", label: "Option 3" },
    { key: "option4", label: "Option 4" },
    { key: "option5", label: "Option 5" },
    { key: "option6", label: "Option 6" },
    { key: "option7", label: "Option 7" },
    { key: "option8", label: "Option 8" },
    { key: "option9", label: "Option 9" },
    { key: "option10", label: "Option 10" },
];
const dropdownSelected = ref(dropdownItems[0]!.key);

// Form field examples
const formFieldName = ref("");
const formFieldEmail = ref("");
const formFieldNotes = ref("");
const formFieldTeam = ref<string>();
const formFieldRater = ref<(typeof defaultCats)[number] | null>(null);
const formFieldFruit = ref<string>();
const formFieldAlignment = ref("center");
const formFieldDigest = ref(false);
const formFieldStructure = ref([0]);
const formFieldTerms = ref(false);
const formFieldDayOptions = ["Monday", "Wednesday", "Friday"];
const formFieldDays = ref<Record<string, boolean>>(Object.fromEntries(formFieldDayOptions.map((day) => [day, false])));
const lastMenuAction = ref("none");
const dropdownMenuSections: DropdownMenuSection[] = [
    {
        id: "actions",
        items: [
            { id: "edit", type: "button", label: "Edit details", icon: "bi bi-pencil" },
            { id: "merge", type: "button", label: "Merge with another person", icon: "bi bi-intersect" },
            { id: "link", type: "anchor", href: "/design-system/", label: "Open as a link", icon: "bi bi-box-arrow-up-right" },
            { id: "unavailable", type: "button", label: "Unavailable action", icon: "bi bi-slash-circle", disabled: true },
            { id: "explained", type: "button", label: "Unavailable, but focusable so its reason is reachable", icon: "bi bi-info-circle", ariaDisabled: true },
        ],
    },
    {
        id: "danger",
        items: [{ id: "remove", type: "button", label: "Remove", icon: "bi bi-trash", theme: "danger" }],
    },
];
const dropdownMenuInstanceSections: DropdownMenuSection[] = [
    {
        id: "instances",
        items: [
            { id: "valence-us", type: "button", label: "Valence US", icon: "bi bi-globe" },
            { id: "current-instance", type: "button", label: "Valence EU", disabled: true, ariaCurrent: true },
        ],
    },
];

const handleDropdownMenuSelect = (item: DropdownMenuItem) => {
    lastMenuAction.value = item.id === "merge" ? "Merge" : item.label;
};

const cats = [
    { id: 1, name: "Leona" },
    { id: 2, name: "Munji" },
    { id: 3, name: "Maru" },
    { id: 4, name: "Nori" },
    { id: 5, name: "Mochi" },
];
const selectedCat = ref(cats[0]);

const simpleCats = cats.map((cat) => cat.name);
const selectedSimpleCat = ref(simpleCats[0]);

const defaultCats = cats.map((cat) => ({ value: cat.id, label: cat.name }));
const selectedDefaultCat = ref(defaultCats[0]);

const selectedActionCat = ref(defaultCats[0]);
const catSearch = ref("");
const lastCatAction = ref<string | null>(null);
const catActions = computed<ComboboxAction[]>(() => {
    const name = catSearch.value.trim();
    const create: ComboboxAction[] =
        name && !defaultCats.some((cat) => cat.label.toLowerCase() === name.toLowerCase()) ? [{ id: "create", label: `Add “${name}” as a new cat` }] : [];
    return [
        ...create,
        {
            id: "adopt",
            label: lastCatAction.value === "adopt" ? "Copied" : "None of these? Copy adoption link",
            closeOnSelect: false,
        },
    ];
});

const isToastOpen = ref(false);
const isCustomToastOpen = ref(false);
const isErrorToastOpen = ref(false);
const isInfoToastOpen = ref(false);
const isWarningToastOpen = ref(false);
const isSuccessToastOpen = ref(false);

const alignments = [
    { key: "left", label: "Left" },
    { key: "center", label: "Center" },
    { key: "right", label: "Right" },
];
const activeAlignment = ref("center");
const activeAlignments = ref(["left", "center"]);

const radioFruitOptions = [
    { value: "apple", label: "Apple" },
    { value: "banana", label: "Banana" },
    { value: "cherry", label: "Cherry", disabled: true },
];
const selectedRadioFruit = ref("apple");
const scaleSliderOptions = [
    { value: -3, label: "Strongly disagree" },
    { value: -2, label: "Disagree" },
    { value: -1, label: "Slightly disagree" },
    { value: 0, label: "Neutral" },
    { value: 1, label: "Slightly agree" },
    { value: 2, label: "Agree" },
    { value: 3, label: "Strongly agree" },
];
const scaleSliderBare = ref<number>();
const scaleSliderAnswer = ref(1);
const selectedRadioAlignment = ref("center");

const isCheckboxChecked = ref(true);

const isSwitchSmChecked = ref(false);
const isSwitchMdChecked = ref(true);
const isSwitchLgChecked = ref(true);
const isSwitchDisabledChecked = ref(false);

const calToday = today(getLocalTimeZone());
const selectedCalDate = shallowRef<DateValue | undefined>(calToday.add({ weeks: 2 }));
const calMinDate = calToday;
const calMaxDate = calToday.add({ days: 30 });

// Diplomats, Explorers, Sentinels, Analysts: the family order of the tonal reference.
const PEOPLE_AVATAR_HUE_ORDER = ["INFJ", "INFP", "ENFJ", "ENFP", "ESTP", "ISTP", "ESFP", "ISFP", "ESFJ", "ISFJ", "ESTJ", "ISTJ", "INTJ", "INTP", "ENTJ", "ENTP"];

function relativeLuminance([r, g, b]: number[]): number {
    const [lr, lg, lb] = [r, g, b].map((channel) => {
        const c = channel / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

function contrastRatio(a: number[], b: number[]): number {
    const [light, dark] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
    return (light + 0.05) / (dark + 0.05);
}

// The light initial is neutral-25; the ink initial is black at 60%, composited onto the fill.
const peopleAvatarHues = PEOPLE_AVATAR_HUE_ORDER.map((letters) => {
    const swatch = getPerspectiveSwatch(letters)!;
    const fill = [1, 3, 5].map((i) => Number.parseInt(swatch.fill.slice(i, i + 2), 16));
    const initial = swatch.peopleInitial === "ink" ? fill.map((channel) => channel * 0.4) : [253, 253, 251];
    return { letters, name: PERSPECTIVE_TYPES[letters].title, contrast: contrastRatio(fill, initial) };
});

const peopleAvatarIdentityRows = [
    { name: "Grace MacNamara", role: "Product Manager", letters: "ESFJ", disabled: false },
    { name: "Gwen", role: "Sales Rep", letters: null, disabled: true },
];

const sliderValue = ref([3]);

const isCheckboxCheckedFilled = ref(true);
const isCheckboxCheckedSquare = ref(true);
const isCheckboxCheckedSquareFilled = ref(true);
const isCheckboxIndeterminate = ref<boolean | "indeterminate">("indeterminate");
const isCheckboxSquareIndeterminate = ref<boolean | "indeterminate">("indeterminate");
const isCardSelected = ref(true);
const isCollapsibleOpen = ref(false);
const isStretchedActionActive = ref(false);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .ds-toolbar {
        @apply sticky top-0 z-20 -mx-6 mb-10 flex flex-col gap-3 border-b border-border bg-surface px-6 py-3 md:-mx-10 md:px-10;
    }

    .ds-pills {
        @apply -mx-6 flex gap-2 overflow-x-auto px-6 md:-mx-10 md:px-10;
        scrollbar-width: none;
    }

    .ds-pills::-webkit-scrollbar {
        display: none;
    }

    .ds-pill {
        @apply shrink-0 rounded-full border border-border bg-surface-raised px-3 py-1 scaffold-details-small text-ink no-underline;
    }

    .ds-avatar-demo {
        @apply flex flex-col items-start gap-2;
    }

    .ds-avatar-caption {
        @apply rounded-[6px] bg-surface-sunken px-2 py-1 scaffold-details-small text-[11px] text-ink-muted;
    }

    .ds-rail {
        @apply hidden flex-col gap-1 lg:sticky lg:top-32 lg:flex;
    }

    .ds-rail-link {
        @apply flex items-center justify-between gap-2 rounded-md border-l-2 border-transparent px-2 py-1 body-small text-ink-muted no-underline hover:bg-surface-hover;
    }

    .ds-rail-link.is-active {
        @apply border-accent text-ink;
    }
}
</style>
